import type {CursorRequest, CursorResponse} from '@/client/mentor/types'
import type {Maybe} from '@/types'
import {PaginationDirection} from '@/client/mentor/types'
import {defaultApi} from '@/axios'
import {safeParseIsoDate} from '@/utils/parse'

export interface ConversationUpdateRequest {
  id: string
  companyId: number
  title: string
}

export interface ConversationFromApi {
  id: string
  companyId: number
  userId: Maybe<number>
  title?: string
  createdAt: string
}

export interface Conversation extends Omit<ConversationFromApi, 'createdAt'> {
  createdAt: Date
}

export class ConversationsClient {
  async getConversation(companyId: number, conversationId: string): Promise<Conversation> {
    return defaultApi
      .get<ConversationFromApi>(`/api/mentor/conversations/${conversationId}`, {params: {companyId}})
      .then((res) => parseConversation(res.data))
  }

  async getConversations(companyId: number, request: CursorRequest<string> = {}): Promise<CursorResponse<Conversation, string>> {
    const {cursor = undefined, limit = undefined, direction = PaginationDirection.DOWN} = request

    return defaultApi
      .get<CursorResponse<ConversationFromApi, string>>('/api/mentor/conversations', {
        params: {companyId, cursor, limit, direction},
      })
      .then((res) => ({...res.data, items: res.data.items.map((item) => parseConversation(item))}))
  }

  async updateConversation(request: ConversationUpdateRequest): Promise<Conversation> {
    return defaultApi
      .put<ConversationFromApi>(`/api/mentor/conversations/${request.id}`, request)
      .then((res) => parseConversation(res.data))
  }

  async deleteConversation(companyId: number, id: string): Promise<boolean> {
    return defaultApi
      .delete<boolean>(`/api/mentor/conversations/${id}`, {
        params: {companyId},
      })
      .then((res) => res.data)
  }
}

export function parseConversation(conversation: ConversationFromApi): Conversation {
  return {
    ...conversation,
    createdAt: safeParseIsoDate(conversation.createdAt),
  }
}

const conversationsClient = new ConversationsClient()

export default conversationsClient
